import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '../../lib/utils';

interface ProgressiveImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string; // Low-res version or static placeholder
  blurDataURL?: string; // Base64 encoded blur placeholder
  onLoad?: () => void;
  onError?: (error: any) => void;
  priority?: boolean; // For above-the-fold images (load immediately)
  loading?: 'lazy' | 'eager';
  aspectRatio?: string; // CSS aspect-ratio value (e.g., "1/1", "16/9")
  fallbackSrc?: string; // Fallback image on error
  quality?: 'low' | 'medium' | 'high'; // Affects loading strategy
  enableBlur?: boolean; // Enable/disable blur effect
  transitionDuration?: number; // Transition duration in ms
}

// Generate a CSS blur placeholder for immediate display
const generateBlurPlaceholder = (color: string = '#f3f4f6'): string => {
  return `data:image/svg+xml;base64,${btoa(`
    <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="blur">
          <feGaussianBlur stdDeviation="2"/>
        </filter>
      </defs>
      <rect width="100%" height="100%" fill="${color}" filter="url(#blur)"/>
    </svg>
  `)}`;
};

// Create intersection observer for lazy loading
const createIntersectionObserver = (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: IntersectionObserverInit
) => {
  if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
    return new IntersectionObserver(callback, {
      rootMargin: '100px', // Start loading 100px before element is visible
      threshold: 0.1,
      ...options,
    });
  }
  return null;
};

export const ProgressiveImage = React.forwardRef<HTMLImageElement, ProgressiveImageProps>(
  ({
    src,
    alt,
    className,
    placeholder,
    blurDataURL,
    onLoad,
    onError,
    priority = false,
    loading = 'lazy',
    aspectRatio,
    fallbackSrc = '/food.jpg', // Default fallback for Nosh app
    quality = 'medium',
    enableBlur = true,
    transitionDuration = 300,
    ...props
  }, ref) => {
    const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
    const [currentSrc, setCurrentSrc] = useState<string>('');
    const [shouldLoad, setShouldLoad] = useState(priority || loading === 'eager');
    
    const imgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // Generate default blur placeholder if none provided
    const defaultBlurPlaceholder = generateBlurPlaceholder('#f3f4f6');
    const blurSrc = blurDataURL || defaultBlurPlaceholder;

    // Set up intersection observer for lazy loading
    useEffect(() => {
      if (shouldLoad || !containerRef.current) return;

      const observer = createIntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setShouldLoad(true);
              if (observerRef.current) {
                observerRef.current.disconnect();
              }
            }
          });
        }
      );

      if (observer && containerRef.current) {
        observer.observe(containerRef.current);
        observerRef.current = observer;
      }

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, [shouldLoad]);

    // Handle image loading
    useEffect(() => {
      if (!shouldLoad || !src) return;

      const img = new Image();
      
      const handleLoad = () => {
        setCurrentSrc(src);
        setImageStatus('loaded');
        onLoad?.();
      };

      const handleError = (error: any) => {
        console.warn('ProgressiveImage: Failed to load image:', src);
        setCurrentSrc(fallbackSrc);
        setImageStatus('error');
        onError?.(error);
      };

      img.onload = handleLoad;
      img.onerror = handleError;
      img.src = src;

      // Cleanup
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }, [shouldLoad, src, fallbackSrc, onLoad, onError]);

    // Determine which image to show
    const displaySrc = () => {
      if (imageStatus === 'loaded' && currentSrc) {
        return currentSrc;
      }
      if (imageStatus === 'error' && fallbackSrc) {
        return fallbackSrc;
      }
      if (placeholder && shouldLoad) {
        return placeholder;
      }
      return enableBlur ? blurSrc : fallbackSrc;
    };

    // CSS classes for smooth transitions
    const containerClasses = cn(
      'relative overflow-hidden bg-gray-100 dark:bg-gray-800',
      aspectRatio && `aspect-[${aspectRatio}]`,
      className
    );

    const imageClasses = cn(
      'w-full h-full object-cover transition-all duration-300 ease-out',
      imageStatus === 'loading' && enableBlur && 'blur-sm scale-110 opacity-90',
      imageStatus === 'loaded' && 'blur-0 scale-100 opacity-100',
      imageStatus === 'error' && 'blur-0 scale-100 opacity-100'
    );

    const overlayClasses = cn(
      'absolute inset-0 bg-gray-200 dark:bg-gray-700 transition-opacity duration-300',
      imageStatus === 'loaded' ? 'opacity-0' : 'opacity-100'
    );

    return (
      <div 
        ref={containerRef}
        className={containerClasses}
        style={{
          aspectRatio: aspectRatio || undefined,
        }}
      >
        {/* Loading overlay with shimmer effect */}
        {imageStatus === 'loading' && (
          <div className={overlayClasses}>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </div>
        )}

        {/* Main image */}
        <img
          ref={ref}
          src={displaySrc()}
          alt={alt}
          className={imageClasses}
          style={{
            transitionDuration: `${transitionDuration}ms`,
          }}
          loading={priority ? 'eager' : 'lazy'}
          onLoad={() => {
            // Additional load handler for the img element itself
            if (imageStatus !== 'loaded') {
              setImageStatus('loaded');
            }
          }}
          onError={() => {
            // Additional error handler for the img element itself
            if (imageStatus !== 'error') {
              setImageStatus('error');
              setCurrentSrc(fallbackSrc);
            }
          }}
          {...props}
        />

        {/* Loading indicator for important images */}
        {priority && imageStatus === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {/* Error state indicator */}
        {imageStatus === 'error' && (
          <div className="absolute top-2 right-2 bg-red-500/80 text-white text-xs px-2 py-1 rounded">
            Failed to load
          </div>
        )}
      </div>
    );
  }
);

ProgressiveImage.displayName = 'ProgressiveImage';

// Hook for preloading critical images
export const useImagePreloader = (imageSrcs: string[]) => {
  useEffect(() => {
    imageSrcs.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [imageSrcs]);
};

// Utility function to generate low-quality image data URL
export const generateLowQualityPlaceholder = (
  width: number = 40,
  height: number = 40,
  color: string = '#f3f4f6'
): string => {
  if (typeof window === 'undefined') return '';
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);
  
  // Add subtle noise/texture
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  for (let i = 0; i < data.length; i += 4) {
    const noise = Math.random() * 10 - 5;
    data[i] = Math.max(0, Math.min(255, data[i] + noise));     // R
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise)); // G
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise)); // B
  }
  
  ctx.putImageData(imageData, 0, 0);
  
  return canvas.toDataURL('image/jpeg', 0.1); // Very low quality
};

export default ProgressiveImage; 